import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import InputField from '@components/InputField';
import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import { LOCATIONS_MODAL_STATES, LOCATIONS_TAG } from '@features/design/utils/designUtils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addNewLocation,
	editLocation,
	getAllLocationsTag,
	getLocationById,
	getSampleCountryList
} from '@services/designInfoUpdateService';

import { isAdmin, printApiError } from '@utils/Util';

const AddLocationModal = ({
	onClose,
	locationsList,
	fetchSampleLocations,
	selectdLocationToEdit,
	setShowAddLocationModal,
	setAllSampleList = () => {},
	activeRow,
	onSuccess = () => {},
	parentComponent
}) => {
	const [locationDetails, setLocationDetails] = useState(LOCATIONS_MODAL_STATES);
	const dispatch = useDispatch();
	const [buildingList, setBuildingList] = useState([]);
	const [roomList, setRoomList] = useState([]);
	const [floorList, setFloorList] = useState([]);
	const [colorList, setColorList] = useState([]);
	const [countryList, setCountryList] = useState([]);
	const [isOnTheWay, setIsOnTheWay] = useState(false);

	const fetchCountryList = async () => {
		try {
			dispatch(openLoader());
			const response = await getSampleCountryList();
			setCountryList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const fetchLocationById = async (locationId) => {
		try {
			dispatch(openLoader());
			const response = await getLocationById(locationId);
			setIsOnTheWay(response?.isOnTheWay);
			const formatData = {
				buildingTag: response?.buildingTagResponse,
				floorTag: response?.floorTagResponse,
				roomNoOrAreaTag: response?.roomOrAreaTagResponse,
				countryId: response?.countryResponse?.id,
				colorTag: response?.colorTagResponse,
				...response
			};
			setLocationDetails(formatData);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (selectdLocationToEdit?.isEdit) {
			fetchLocationById(selectdLocationToEdit?.id);
		}
	}, [selectdLocationToEdit?.isEdit]);

	const fetchBuildingTags = async (searchKey = '') => {
		try {
			dispatch(openLoader());
			const params = `?type=${LOCATIONS_TAG.SAMPLE_INVENTORY_LOCATION_BUILDING_TAG}&search=${searchKey}`;
			const response = await getAllLocationsTag(params);
			setBuildingList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const fetchRoomTags = async (searchKey = '') => {
		try {
			dispatch(openLoader());
			const params = `?type=${LOCATIONS_TAG.SAMPLE_INVENTORY_LOCATION_ROOM_OR_AREA_TAG}&search=${searchKey}`;
			const response = await getAllLocationsTag(params);
			setRoomList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};
	const fetchFloorTags = async (searchKey = '') => {
		try {
			dispatch(openLoader());
			const params = `?type=${LOCATIONS_TAG.SAMPLE_INVENTORY_LOCATION_FLOOR_TAG}&search=${searchKey}`;
			const response = await getAllLocationsTag(params);
			setFloorList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const fetchColorTags = async (searchKey = '') => {
		try {
			dispatch(openLoader());
			const params = `?type=${LOCATIONS_TAG.SAMPLE_INVENTORY_LOCATION_COLOR_TAG}&search=${searchKey}`;
			const response = await getAllLocationsTag(params);
			setColorList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchCountryList();
		fetchBuildingTags();
		fetchFloorTags();
		fetchRoomTags();
		fetchColorTags();
	}, []);

	useEffect(() => {
		setLocationDetails((prev) => ({
			...prev,
			locationId: selectdLocationToEdit?.locationId || selectdLocationToEdit
		}));
	}, [selectdLocationToEdit?.locationId]);

	const handleChange = (e) => {
		const { value, name } = e.target;
		setLocationDetails((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleSelectedOption = (value, name, isObject = true) => {
		setLocationDetails((prev) => ({
			...prev,
			[name]: isObject ? { id: value?.id, text: value?.text, colorCode: value?.colorCode } : value
		}));
	};

	const isEmptyObject = (value) => {
		return typeof value === 'object' && value !== null && Object.keys(value).length === 0;
	};

	const removeFalsyAndEmptyObjects = (obj) => {
		return Object.fromEntries(
			Object.entries(obj).filter(([, value]) => Boolean(value) && !isEmptyObject(value))
		);
	};

	const fetchSingleLocation = async (locId) => {
		try {
			dispatch(openLoader());
			const response = await getLocationById(locId);
			if (parentComponent === 'INVENTORY_MODAL') {
				setAllSampleList((prev) => {
					return prev.map((product) => {
						return {
							...product,
							sampleList: product?.sampleList.map((item) => {
								if (item?.rowId === activeRow) {
									return {
										...item,
										sampleInventoryLocationResponse: {
											...response,
											value: response?.locationId
										}
									};
								} else {
									return item;
								}
							})
						};
					});
				});
			} else {
				setAllSampleList((prev) => {
					return prev.map((item) => {
						if (activeRow === item?.rowId) {
							return {
								...item,
								sampleInventoryLocationResponse: { ...response, value: response?.locationId }
							};
						} else {
							return item;
						}
					});
				});
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSubmit = async () => {
		try {
			dispatch(openLoader());
			const cleanedBody = removeFalsyAndEmptyObjects({ ...locationDetails });
			const response = selectdLocationToEdit?.isEdit
				? await editLocation({ ...cleanedBody, isOnTheWay }, selectdLocationToEdit?.id)
				: await addNewLocation({ ...cleanedBody, isOnTheWay });
			if (response) {
				fetchSingleLocation(response?.id);
				onSuccess({
					...response,
					isEdit: selectdLocationToEdit?.isEdit
				});
				toastSuccess('Successful!');
				setShowAddLocationModal(false);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const findCountryName = () =>
		countryList.find((country) => country?.id === locationDetails?.countryId) || {};

	return (
		<ModalContainer onCloseModal={onClose} className="add-location-modal">
			<div className="popup-wrapper shadow-lg">
				<div className="common-popup add-location-modal">
					<div className="common-popup-body">
						<div className="row">
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">*Location Id</label>
									<CustomDropdown
										title="Select"
										items={locationsList}
										isShowSearch
										onItemSearch={(value) =>
											fetchSampleLocations && fetchSampleLocations(value)
										}
										selectedItem={locationDetails?.locationId}
										onItemClick={(value) =>
											handleSelectedOption(value?.id, 'sampleId', false)
										}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">Country</label>
									<CustomDropdown
										title="Select"
										selectedItem={findCountryName()?.name}
										items={countryList}
										onItemClick={(value) =>
											handleSelectedOption(value?.id, 'countryId', false)
										}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">Building Name</label>
									<CustomDropdown
										onAddNewButtonClick={(value, setIsOpen) => {
											handleSelectedOption({ text: value }, 'buildingTag');
											setIsOpen(false);
										}}
										showAddNewButton={isAdmin()}
										selectedItem={locationDetails?.buildingTag?.text}
										title="Select"
										items={buildingList}
										onItemClick={(value) => handleSelectedOption(value, 'buildingTag')}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">Floor</label>
									<CustomDropdown
										isShowSearch={false}
										selectedItem={locationDetails?.floorTag?.text}
										title="Select"
										items={floorList}
										onItemClick={(value) => handleSelectedOption(value, 'floorTag')}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">Room no/area</label>
									<CustomDropdown
										onAddNewButtonClick={(value, setIsOpen) => {
											handleSelectedOption({ text: value }, 'roomNoOrAreaTag');
											setIsOpen(false);
										}}
										selectedItem={locationDetails?.roomNoOrAreaTag?.text}
										showAddNewButton={isAdmin()}
										items={roomList}
										title="Select"
										onItemClick={(value) =>
											handleSelectedOption(value, 'roomNoOrAreaTag')
										}
									/>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label htmlFor="">Color</label>
									<CustomDropdown
										onAddNewButtonClick={(value, setIsOpen) => {
											handleSelectedOption({ text: value }, 'colorTag');
											setIsOpen(false);
										}}
										selectedItem={locationDetails?.colorTag?.text}
										showAddNewButton={isAdmin()}
										items={colorList}
										title="Select"
										onItemClick={(value) => handleSelectedOption(value, 'colorTag')}
									/>
								</div>
							</div>
							<div className="col-md-12">
								<InputField
									type="text"
									value={locationDetails.otherDetails}
									onChangeHandler={handleChange}
									name="otherDetails"
									id="otherDetails"
									labelTitle="Store type details"
									placeholder="Rack/Box/Covered details"
								/>
							</div>
						</div>
					</div>
					<div className="modal-footer d-flex align-items-center justify-content-between">
						<div className="custom-chekbox">
							<div className="form-group mb-0">
								<input
									type="checkbox"
									id="way"
									name="isOnTheWay"
									checked={isOnTheWay}
									disabled={!isAdmin()}
									value={isOnTheWay}
									onChange={() => setIsOnTheWay(!isOnTheWay)}
								/>
								<label className="regular-14 d-flex align-items-center" for="way">
									<span>On the way</span>
								</label>
							</div>
						</div>
						<div className="footer-buttons text-right">
							<button className="button gray-outline size32" onClick={onClose}>
								Cancel
							</button>
							<button className="button size32 ml-2" onClick={handleSubmit}>
								Submit
							</button>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddLocationModal;
